import { useState, useEffect, useRef } from 'react';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

type MediaType = 'image' | 'video' | 'document' | 'audio';

const getValidFileTypes = (type: MediaType) => {
  switch (type) {
    case 'image':
      return ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    case 'video':
      return ['video/mp4'];
    case 'audio':
      return ['audio/mp3'];
    case 'document':
      return ['application/pdf'];
    default:
      return [];
  }
};

export const useFileUpload = (type: MediaType) => {
  const { apiService } = useEditorStore();
  const [uploading, setUploading] = useState(false);
  const [uploadText, setUploadText] = useState('Uploading');
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateFile = (file: File) => {
    const validTypes = getValidFileTypes(type);
    if (!validTypes.includes(file.type)) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Invalid file type'
      });
      return false;
    }
    return true;
  };

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setUploading(false);
    }
  };

  const uploadFile = async (file: File) => {
    if (!validateFile(file)) {
      return null;
    }

    setUploading(true);
    abortControllerRef.current = new AbortController();

    const mediaTypes =
      type === 'document'
        ? 'documents'
        : type === 'image'
          ? 'pictures'
          : type === 'video'
            ? 'videos'
            : 'audios';

    try {
      const response = await apiService?.uploadMedia(
        mediaTypes,
        file,
        abortControllerRef.current.signal
      );

      if (response?.error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: response.message
        });
        return null;
      }

      const mediaType =
        type === 'document'
          ? 'document'
          : type === 'image'
            ? 'picture'
            : type === 'video'
              ? 'video'
              : 'audio';

      return response?.[mediaType] || null;
    } catch {
      return null;
    } finally {
      abortControllerRef.current = null;
      setUploading(false);
    }
  };

  // Effect for upload text animation
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (uploading) {
      interval = setInterval(() => {
        setUploadText(prev => {
          if (prev === 'Uploading...') {
            return 'Uploading';
          }
          return prev + '.';
        });
      }, 250);
    } else {
      setUploadText('Uploading');
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [uploading]);

  return {
    uploadFile,
    uploading,
    uploadText,
    cancelUpload
  };
};
